import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { StatusDot } from "../components/StatusDot.tsx";
import {
	api,
	type AgentDirectoryEntry,
	type AgentInstance,
	type AgentStatusForLabel,
} from "../lib/api.ts";
import { formatHostDetail } from "../lib/time.ts";

/**
 * Host-centric fleet view — inverse of /agents. Each card is a physical
 * machine (hostId) listing the agents currently running on it with their
 * version + live telemetry detail (currentTask / lastError / idle).
 *
 * Backend already returns everything we need via /api/agents/status — this
 * page just inverts the {label → instances[]} map to {hostId → instances[]}.
 */
export function Hosts() {
	const [agents, setAgents] = useState<AgentDirectoryEntry[] | null>(null);
	const [statuses, setStatuses] = useState<AgentStatusForLabel[] | null>(null);
	const [expectedCliVersion, setExpectedCliVersion] = useState<string | undefined>(undefined);
	const [deploySha, setDeploySha] = useState<string | undefined>(undefined);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		api
			.listAgents()
			.then((r) => setAgents(r.agents))
			.catch((err) => setError((err as Error).message));
	}, []);

	useEffect(() => {
		let cancelled = false;
		async function tick() {
			try {
				const r = await api.listAllAgentStatuses();
				if (cancelled) return;
				setStatuses(r.statuses);
				setExpectedCliVersion(r.expectedCliVersion);
				setDeploySha(r.deploySha);
			} catch {
				/* keep prior */
			}
		}
		tick();
		const id = setInterval(tick, 10_000);
		return () => {
			cancelled = true;
			clearInterval(id);
		};
	}, []);

	const labelToDisplay = useMemo(() => {
		const m = new Map<string, string>();
		for (const a of agents ?? []) m.set(a.key, a.displayName);
		return m;
	}, [agents]);

	const hosts = useMemo(() => buildHosts(statuses ?? [], labelToDisplay), [statuses, labelToDisplay]);

	const totalOnline = useMemo(
		() => hosts.reduce((n, h) => n + h.instances.filter((i) => i.online).length, 0),
		[hosts],
	);

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-2xl font-semibold text-zinc-100">Hosts</h1>
					<p className="text-sm text-zinc-500 mt-1">
						{statuses === null
							? "Loading…"
							: hosts.length === 0
								? "No agents running anywhere right now."
								: `${totalOnline} agent${totalOnline === 1 ? "" : "s"} online across ${hosts.length} host${hosts.length === 1 ? "" : "s"}`}
					</p>
				</div>
				<div className="flex items-baseline gap-3">
					{expectedCliVersion && (
						<span
							className="text-[11px] text-zinc-600 font-mono"
							title={`Worker bundle git SHA${expectedCliVersion ? ` · CLI v${expectedCliVersion}` : ""}`}
						>
							Worker SHA: <span className="text-zinc-400">{deploySha ?? "?"}</span>
						</span>
					)}
					<Link to="/agents" className="text-xs text-zinc-500 hover:text-zinc-300">
						View by agent →
					</Link>
				</div>
			</div>

			{error && (
				<div className="mb-4 px-3 py-2 rounded border border-rose-800/40 bg-rose-900/20 text-xs text-rose-300">
					{error}
				</div>
			)}

			{hosts.length === 0 && statuses !== null && (
				<div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-lg px-6 py-8 text-center">
					<p className="mb-2">No agents are running right now.</p>
					<p className="font-mono text-xs">
						Start one with{" "}
						<code className="px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-300">
							npx -y @theg1group/agent@latest run &lt;label&gt;
						</code>{" "}
						on any machine.
					</p>
				</div>
			)}

			<div className="space-y-4">
				{hosts.map((h) => (
					<HostCard
						key={h.hostId}
						host={h}
						labelToDisplay={labelToDisplay}
						expectedCliVersion={expectedCliVersion}
					/>
				))}
			</div>
		</div>
	);
}

interface HostGroup {
	hostId: string;
	hostname: string;
	platform: string;
	arch: string;
	instances: AgentInstance[];
}

function buildHosts(
	statuses: AgentStatusForLabel[],
	_labelToDisplay: Map<string, string>,
): HostGroup[] {
	const byHost = new Map<string, HostGroup>();
	for (const s of statuses) {
		for (const inst of s.instances) {
			let g = byHost.get(inst.hostId);
			if (!g) {
				g = {
					hostId: inst.hostId,
					hostname: inst.hostname,
					platform: inst.platform,
					arch: inst.arch,
					instances: [],
				};
				byHost.set(inst.hostId, g);
			}
			g.instances.push(inst);
		}
	}
	// Sort hosts: most-stuck first → most-online next → alphabetical hostname.
	return Array.from(byHost.values()).sort((a, b) => {
		const sa = a.instances.filter((i) => i.online && i.stuck).length;
		const sb = b.instances.filter((i) => i.online && i.stuck).length;
		if (sa !== sb) return sb - sa;
		const oa = a.instances.filter((i) => i.online).length;
		const ob = b.instances.filter((i) => i.online).length;
		if (oa !== ob) return ob - oa;
		return a.hostname.localeCompare(b.hostname);
	});
}

function HostCard({
	host,
	labelToDisplay,
	expectedCliVersion,
}: {
	host: HostGroup;
	labelToDisplay: Map<string, string>;
	expectedCliVersion: string | undefined;
}) {
	const online = host.instances.filter((i) => i.online);
	const offline = host.instances.filter((i) => !i.online);
	const stuck = online.filter((i) => i.stuck);
	const outdated = expectedCliVersion
		? online.filter((i) => i.version !== expectedCliVersion)
		: [];

	const summaryColor = stuck.length > 0 ? "amber" : online.length > 0 ? "emerald" : "zinc";

	// Sort agents within the card: stuck first, then online, then offline; alphabetical within.
	const sortedInstances = [...host.instances].sort((a, b) => {
		const ra = a.online && a.stuck ? 0 : a.online ? 1 : 2;
		const rb = b.online && b.stuck ? 0 : b.online ? 1 : 2;
		if (ra !== rb) return ra - rb;
		return (labelToDisplay.get(a.label) ?? a.label).localeCompare(
			labelToDisplay.get(b.label) ?? b.label,
		);
	});

	return (
		<div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
			<div className="px-4 py-3 border-b border-zinc-800/60 flex items-center justify-between">
				<div>
					<div className="flex items-center gap-2 text-zinc-100">
						<span className="font-mono text-sm">{host.hostname}</span>
						<span className="text-[11px] text-zinc-500">
							{host.platform}/{host.arch}
						</span>
					</div>
					<div className="mt-0.5">
						<StatusDot color={summaryColor} text={summaryText(online.length, stuck.length, offline.length)} />
					</div>
				</div>
				<div className="text-[11px] text-zinc-600 font-mono" title={host.hostId}>
					{host.hostId.slice(0, 8)}…
				</div>
			</div>

			{outdated.length > 0 && expectedCliVersion && (
				<div className="px-4 py-2 bg-rose-900/30 border-b border-rose-800/40 text-[11px] text-rose-300 font-mono">
					⚠ {outdated.length === 1 ? "1 agent" : `${outdated.length} agents`} on this host running outdated CLI — latest is{" "}
					<span className="text-rose-200">v{expectedCliVersion}</span>. Restart with{" "}
					<code className="bg-rose-950/50 px-1 rounded">npx -y @theg1group/agent@latest run &lt;label&gt;</code>
				</div>
			)}

			<div className="divide-y divide-zinc-800/60">
				{sortedInstances.map((inst) => (
					<AgentRow
						key={`${inst.hostId}:${inst.label}`}
						instance={inst}
						displayName={labelToDisplay.get(inst.label) ?? inst.label}
						expectedCliVersion={expectedCliVersion}
					/>
				))}
			</div>
		</div>
	);
}

function AgentRow({
	instance,
	displayName,
	expectedCliVersion,
}: {
	instance: AgentInstance;
	displayName: string;
	expectedCliVersion: string | undefined;
}) {
	const dotColor = !instance.online ? "zinc" : instance.stuck ? "amber" : "emerald";
	const detail = formatHostDetail(instance);
	const outdated = instance.online && expectedCliVersion && instance.version !== expectedCliVersion;

	return (
		<Link
			to={`/agents/${instance.label}`}
			className="block px-4 py-2 hover:bg-zinc-800/40 transition-colors"
		>
			<div className="flex items-center gap-3">
				<StatusDot color={dotColor} />
				<div className="flex-1 min-w-0">
					<div className="flex items-baseline gap-2">
						<span className="text-sm text-zinc-200">{displayName}</span>
						<span className="text-[11px] text-zinc-500 font-mono">{instance.label}</span>
					</div>
					{detail && (
						<div
							className={`text-[11px] mt-0.5 font-mono truncate ${
								instance.stuck ? "text-amber-400/80" : "text-zinc-500"
							}`}
						>
							{detail}
						</div>
					)}
				</div>
				<span
					className={`text-[11px] font-mono shrink-0 ${outdated ? "text-rose-400" : "text-zinc-600"}`}
					title={outdated ? `outdated · latest v${expectedCliVersion}` : undefined}
				>
					v{instance.version}
					{outdated && " ⚠"}
				</span>
			</div>
		</Link>
	);
}

function summaryText(online: number, stuck: number, offline: number): string {
	const parts: string[] = [];
	if (stuck > 0) parts.push(`${stuck} stuck`);
	if (online - stuck > 0) parts.push(`${online - stuck} online`);
	if (online === 0 && offline > 0) parts.push("offline");
	if (offline > 0 && online > 0) parts.push(`+${offline} stale`);
	return parts.join(" · ") || "—";
}
